import React from 'react';
import { Modal, Button } from '@mui/material';
import styled from 'styled-components';
import COLOR from 'utils/constants/color';
import { firebaseImage } from 'utils/assets/imageConstant/firebaseImage';
import { useDispatch } from 'react-redux';
import { useUI } from 'store/UI/selectors';
import { updateUI } from 'store/UI/actions';
import { useAudioPermissionCheck } from 'components/hooks/useAudioPermission/useAudioPermissionCheck';

const ModalContent = styled.div`
  align-items: center;
  background-color: ${COLOR.white};
  border-radius: 8px;
  display: inline-flex;
  flex-direction: column;
  left: 50%;
  max-width: 400px;
  padding: 24px;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  @media screen and (max-width: 768px) {
    width: 60vw;
  }
  @media screen and (max-width: 480px) {
    width: 80vw;
  }
`;

const ModalRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
  width: 100%;
`;

const ModalTitle = styled.h3`
  color: ${COLOR.black100};
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const ModalMessage = styled.p`
  color: ${COLOR.black500};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

const ModalImage = styled.img`
  width: auto;
  height: 300px;
  margin: 16px 0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  @media screen and (max-width: 768px) {
    height: 200px;
  }
  @media screen and (max-width: 480px) {
    height: 150px;
  }
`;

const getModalMessage = (permissionStatus) => {
  switch (permissionStatus) {
    case 'denied':
      return 'Microphone access is required to record audio answers. Please click "Allow Permission" to grant access.';
    case 'notfound':
      return 'No microphone found. Please connect a microphone device to record audio answers.';
    case 'error':
      return 'Unable to access microphone. Please check your device settings and try again.';
    default:
      return 'This assignment contains audio recording questions. Please allow microphone access to continue.';
  }
};

const MicrophonePermissionModal = ({ grantedAllow }) => {
  const dispatch = useDispatch();
  const { openMicrophonePermissionModal } = useUI();
  const { microphonePermissionStatus, requestMicrophonePermission } = useAudioPermissionCheck();
  return (
    <Modal
      open={openMicrophonePermissionModal}
      onClose={(e) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(updateUI({ openMicrophonePermissionModal: false }));
      }}
      disableEscapeKeyDown={microphonePermissionStatus === 'denied'}
    >
      <ModalContent>
        <ModalTitle>Microphone Permission Required</ModalTitle>
        <ModalMessage>{getModalMessage(microphonePermissionStatus)}</ModalMessage>
        <ModalImage
          src={firebaseImage.microphonePermissionModal}
          alt="How to enable microphone permission"
        />
        <ModalRow>
          <Button
            variant="outlined"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dispatch(updateUI({ openMicrophonePermissionModal: false }));
            }}
            style={{ marginRight: '8px' }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              const granted = await requestMicrophonePermission();
              if (granted) {
                grantedAllow();
              }
            }}
          >
            Allow Permission
          </Button>
        </ModalRow>
      </ModalContent>
    </Modal>
  );
};

export default MicrophonePermissionModal;
